import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

// Hardcoded data
const platformStats = {
  totalUsers: 256,
  activeUsers: 198,
  coursesPublished: 12,
  campaignsActive: 3,
};

const campaignPerformance = [
  { id: 1, name: 'Q3 Security Awareness', sent: 240, opened: 218, clicked: 45, reported: 173 },
  { id: 2, name: 'Password Reset Simulation', sent: 256, opened: 220, clicked: 62, reported: 158 },
  { id: 3, name: 'Invoice Phishing Test', sent: 240, opened: 200, clicked: 28, reported: 172 },
];

const recentActivity = [
  { id: 1, event: 'New user registration', user: 'Sarah Johnson', timestamp: '2023-10-18 09:24:15' },
  { id: 2, event: 'Campaign completed', user: 'Admin', timestamp: '2023-10-17 16:35:22', details: 'Spring Security Training' },
  { id: 3, event: 'Course published', user: 'Admin', timestamp: '2023-10-15 11:12:08', details: 'Advanced Social Engineering' },
  { id: 4, event: 'User completed course', user: 'Michael Chen', timestamp: '2023-10-14 15:47:33', details: 'Email Security Basics' },
];

const Dashboard = () => {
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Simulate data loading
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1000);
    
    return () => clearTimeout(timer);
  }, []);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">
          Admin Dashboard
        </h1>
        <div>
          <Link to="/admin/campaigns/new" className="btn btn-primary">
            New Campaign
          </Link>
        </div>
      </div>
      
      {/* Platform Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="card bg-blue-50 border-l-4 border-blue-500">
          <h3 className="font-semibold text-lg text-gray-800">Total Users</h3>
          <p className="text-2xl font-bold mt-2">{platformStats.totalUsers}</p>
          <p className="text-sm text-gray-500 mt-1">
            {platformStats.activeUsers} active
          </p>
        </div>
        
        <div className="card bg-green-50 border-l-4 border-green-500">
          <h3 className="font-semibold text-lg text-gray-800">Courses</h3>
          <p className="text-2xl font-bold mt-2">{platformStats.coursesPublished}</p>
          <p className="text-sm text-gray-500 mt-1">Published</p>
        </div>
        
        <div className="card bg-yellow-50 border-l-4 border-yellow-500">
          <h3 className="font-semibold text-lg text-gray-800">Active Campaigns</h3>
          <p className="text-2xl font-bold mt-2">{platformStats.campaignsActive}</p>
          <p className="text-sm text-gray-500 mt-1">In progress</p>
        </div>
        
        <div className="card bg-purple-50 border-l-4 border-purple-500">
          <h3 className="font-semibold text-lg text-gray-800">Phishing Score</h3>
          <p className="text-2xl font-bold mt-2">72%</p>
          <p className="text-sm text-gray-500 mt-1">Org. awareness level</p>
        </div>
      </div>
      
      {/* Campaign Performance */}
      <div className="card mb-8">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Recent Campaign Performance</h2>
          <Link to="/admin/campaigns" className="text-sm text-primary-600 hover:text-primary-700">
            View all campaigns
          </Link>
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Campaign
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Sent
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Opened
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Clicked
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Reported
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {campaignPerformance.map((campaign) => (
                <tr key={campaign.id}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {campaign.name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {campaign.sent}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {campaign.opened} ({Math.round((campaign.opened / campaign.sent) * 100)}%)
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-red-600 font-medium">
                      {campaign.clicked} ({Math.round((campaign.clicked / campaign.sent) * 100)}%)
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-green-600 font-medium">
                      {campaign.reported} ({Math.round((campaign.reported / campaign.sent) * 100)}%)
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      
      {/* Recent Activity */}
      <div className="card">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Recent Activity</h2>
          <Link to="/admin/reports" className="text-sm text-primary-600 hover:text-primary-700">
            View all activity
          </Link>
        </div>
        
        <div className="overflow-hidden">
          <ul className="divide-y divide-gray-200">
            {recentActivity.map((activity) => (
              <li key={activity.id} className="py-4">
                <div className="flex space-x-3">
                  <div className="flex-1 space-y-1">
                    <div className="flex items-center justify-between">
                      <h3 className="text-sm font-medium">{activity.event}</h3>
                      <p className="text-sm text-gray-500">{activity.timestamp}</p>
                    </div>
                    <p className="text-sm text-gray-500">
                      By: {activity.user}
                      {activity.details && ` | ${activity.details}`}
                    </p>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default Dashboard; 